/**
 * Mews Payments checkout (https://docs.mews.com/mews-payments-checkout).
 *
 * The same script serves the demo and production: only `dataBaseUrl` points
 * the checkout to the Mews demo, and it must stay unset in production.
 */
export const MEWS_CHECKOUT_SCRIPT_URL = 'https://cdn.mews.com/payments/checkout-embed.js';
const scripts = new Map();
/** The global is set asynchronously once the script has run. */
function waitForCheckout(timeoutMs) {
    const start = Date.now();
    return new Promise((resolve, reject) => {
        const check = () => {
            var _a;
            if ((_a = window.Mews) === null || _a === void 0 ? void 0 : _a.PaymentCheckout) {
                resolve();
            }
            else if (Date.now() - start > timeoutMs) {
                reject(new Error('Mews checkout did not initialize'));
            }
            else {
                window.setTimeout(check, 50);
            }
        };
        check();
    });
}
/**
 * Loads the checkout script once per page.
 *
 * @function loadMewsCheckout
 * @since 1.0.0
 */
export function loadMewsCheckout(src = MEWS_CHECKOUT_SCRIPT_URL) {
    const cached = scripts.get(src);
    if (cached) {
        return cached;
    }
    const promise = new Promise((resolve, reject) => {
        var _a;
        const existing = document.querySelector(`script[src="${src}"]`);
        const script = existing !== null && existing !== void 0 ? existing : document.createElement('script');
        if (!existing) {
            script.src = src;
            script.async = true;
            document.head.appendChild(script);
        }
        else if ((_a = window.Mews) === null || _a === void 0 ? void 0 : _a.PaymentCheckout) {
            resolve();
            return;
        }
        script.addEventListener('load', () => resolve());
        script.addEventListener('error', () => reject(new Error('Mews checkout script failed to load')));
    })
        .then(() => waitForCheckout(5000))
        .catch((error) => {
        scripts.delete(src);
        throw error;
    });
    scripts.set(src, promise);
    return promise;
}
/**
 * Removes the checkout iframe. Never throws: it runs on unmount.
 *
 * @function destroyMewsCheckout
 * @since 1.0.0
 */
export function destroyMewsCheckout() {
    var _a, _b, _c;
    try {
        (_c = (_b = (_a = window.Mews) === null || _a === void 0 ? void 0 : _a.PaymentCheckout) === null || _b === void 0 ? void 0 : _b.destroy) === null || _c === void 0 ? void 0 : _c.call(_b);
    }
    catch (error) {
        // The iframe may already be gone
    }
}
/**
 * Mews language code of an app language (Mews has no fr-CA).
 *
 * @function mewsLanguageCode
 * @since 1.0.0
 */
export function mewsLanguageCode(language) {
    switch ((language !== null && language !== void 0 ? language : '').substring(0, 2).toLowerCase()) {
        case 'fr':
            return 'fr-FR';
        case 'es':
            return 'es-ES';
        default:
            return 'en-US';
    }
}
